<script setup lang="ts">
import { $isLinkNode } from "@lexical/link";
import { mergeRegister } from "@lexical/utils";
import { useIntersectionObserver, useThrottle } from "@vueuse/core";
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  BLUR_COMMAND,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  KEY_SPACE_COMMAND,
  type LexicalCommand,
  type LexicalNode,
  type RangeSelection,
  TextNode,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";

import { backend } from "~/api";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import { LEXICAL_OUTSIDE_CLASS, RECOMMENDATION_SECTION_KEY_AI } from "~/components/text/const";
import { $createTypeaheadNode, $isTypeaheadNode, TypeaheadNode } from "~/components/text/nodes/TypeaheadNode";
import { isDefined } from "~/shared/common";
import { DropdownMenuItemKind, EventKind, Placement, TypeaheadSpecialEntity } from "~/shared/enums";
import {
  type DropdownMenuSection,
  type TypeaheadGetOptionsFn,
  type TypeaheadGetOptionsResult,
  type TypeaheadOption,
} from "~/shared/types";
import { usePageStore } from "~/stores";
import { asyncState } from "~/utils/async";
import { when } from "~/utils/wait";

import { usePrefixTypeaheadTriggerMatch } from "./common";
import TypeaheadEntityItem from "./TypeaheadEntityItem.vue";
import TypeaheadSubmenu from "./TypeaheadSubmenu.vue";

type TypeaheadState = (TypeaheadGetOptionsResult | null)[];

const props = defineProps<{
  trigger: string;
  getOptionsFns: TypeaheadGetOptionsFn[];
  wide?: boolean;
}>();

const pageStore = usePageStore();

const emit = defineEmits<{
  select: [selection: RangeSelection, option: TypeaheadOption, parentNode: LexicalNode | null];
}>();

const editor = useLexicalComposer();
const dropdownRef = ref<InstanceType<typeof DropdownMenu> | null>(null);
const scrollContainer = computed<HTMLDivElement | null>(() => dropdownRef.value?.scrollContainer ?? null);

const abandonedNodeKeys = new Set<string>();
const typeaheadNodeKey = ref<string | null>(null);
const typeaheadNodeElement = ref<Element | null>(null);
const query = ref("");

const maxHeightPixels = ref<number | undefined>(undefined);
const skidding = ref(0);

const placement = computed(() => (pageStore.isMobile ? Placement.TOP : Placement.BOTTOM_LEFT));

// Loads options with a throttle
const queryThrottled = useThrottle(query, 500);
const previousQuery = ref(query.value);
const isLoadingInner = ref(false);
const loadingTimeout = ref<NodeJS.Timeout | null>(null);
const {
  state: allOptionsRaw,
  isLoading: isLoadingOuter,
  execute,
} = asyncState<TypeaheadState>(
  async (setState) => {
    if (!typeaheadNodeKey.value) {
      return [];
    }

    const queryNorm = queryThrottled.value.toLowerCase();
    const prevQuery = previousQuery.value;
    const isMoreSpecificQuery = !!prevQuery && queryNorm.includes(prevQuery);
    const previousResults: TypeaheadState = allOptionsRaw.value;

    previousQuery.value = queryNorm;
    isLoadingInner.value = true;
    if (loadingTimeout.value) {
      clearTimeout(loadingTimeout.value);
    }

    // Set partial state as options are loaded
    const results = await Promise.allSettled(
      props.getOptionsFns.map(async (promise, index) => {
        const updateState = (result: TypeaheadState[number]) => {
          setState((currentState) => {
            let newState = [...currentState];
            if (newState.length === 0) {
              newState = Array(props.getOptionsFns.length).fill(null);
            }
            newState[index] = result;
            return newState;
          });
          return result;
        };

        // Cache the results if we already loaded all for a specific query
        const prevResult = previousResults[index];
        if (isMoreSpecificQuery && prevResult && (!prevResult.total || prevResult.options.length >= prevResult.total)) {
          return updateState(prevResult);
        }

        const result = await promise(queryNorm);
        return updateState(result);
      })
    );

    // oxlint-disable-next-line dart/no-set-timeout
    loadingTimeout.value = setTimeout(() => {
      isLoadingInner.value = false;
    });
    return results.map((result) => (result.status === "fulfilled" ? result.value : null));
  },
  { default: [], watch: queryThrottled, preserveState: true, noInitialExecution: true }
);
const allOptions = computed(() => allOptionsRaw.value.filter(isDefined));
const isLoading = computed(() => isLoadingInner.value || isLoadingOuter.value);

const optionsFiltered = computed(() => {
  const queryNorm = query.value.toLowerCase().trim();
  if (queryNorm.length === 0) {
    return allOptions.value;
  }

  return allOptions.value.map((e) => {
    const options = e.options.filter(
      (f) =>
        f.label.toLowerCase().includes(queryNorm) || f.adtlSearchTerms?.some((t) => t.toLowerCase().includes(queryNorm))
    );
    return { ...e, options };
  });
});

const onSelectOption = (option: TypeaheadOption) => {
  editor.getRootElement()?.focus({ preventScroll: true });
  editor.update(() => {
    if (!typeaheadNodeKey.value) {
      return;
    }
    const selection = $getSelection();
    const typeaheadNode = $getNodeByKey(typeaheadNodeKey.value);
    if (!typeaheadNode || !$isTypeaheadNode(typeaheadNode) || !$isRangeSelection(selection)) {
      return;
    }

    emit("select", selection, option, typeaheadNode.getParent());
    typeaheadNode.remove();

    if (pageStore.isPublic) {
      return;
    }

    backend.event.create(EventKind.USAGE_NLP_TYPEAHEAD_ACCEPT, {
      via: props.trigger,
      propertyKind: option.property?.kind,
      value: option.value,
    });
  });
};

const itemRefMap = reactive(new Map<string, HTMLElement>());
const assignItemRef = (element: HTMLElement, item: TypeaheadOption, key: string) => {
  const items = optionsFiltered.value.find((e) => e.key === key);
  if (!items) {
    return;
  }

  const isLastOfKind = items.options[items.options.length - 1] === item;
  if (!isLastOfKind) {
    return;
  }

  itemRefMap.set(key, element);
  element.setAttribute("data-entity", key);
};

const sections = computed<DropdownMenuSection[]>(() =>
  optionsFiltered.value
    .filter(({ options }) => options.length > 0)
    .map(({ options, key, title }) => {
      const items = options.map((option) =>
        option.options
          ? {
              title: option.label,
              kind: DropdownMenuItemKind.COMPONENT,
              strikethrough: option.strikethrough,
              disabled: option.disabled,
              disabledReason: option.disabledReason,
              component: TypeaheadSubmenu,
              componentArgs: { item: option, onSelect: (e: TypeaheadOption) => onSelectOption(e) },
              onAssignRef: (e: HTMLElement) => assignItemRef(e, option, key),
            }
          : option.specialEntity === TypeaheadSpecialEntity.TASK ||
              option.specialEntity === TypeaheadSpecialEntity.DOC ||
              option.specialEntity === TypeaheadSpecialEntity.SKILL ||
              option.specialEntity === TypeaheadSpecialEntity.USER
            ? {
                title: option.label,
                kind: DropdownMenuItemKind.COMPONENT,
                disabled: option.disabled,
                disabledReason: option.disabledReason,
                component: TypeaheadEntityItem,
                componentArgs: { item: option, onSelect: (e: TypeaheadOption) => onSelectOption(e) },
                onAssignRef: (e: HTMLElement) => assignItemRef(e, option, key),
              }
            : {
                title: option.label,
                kind: DropdownMenuItemKind.BUTTON,
                icon: option.icon,
                iconArgs: option.iconArgs,
                strikethrough: option.strikethrough,
                disabled: option.disabled,
                disabledReason: option.disabledReason,
                onClick: () => onSelectOption(option),
                onAssignRef: (e: HTMLElement) => assignItemRef(e, option, key),
              }
      );

      return { title: title || key, items, showTitle: key !== RECOMMENDATION_SECTION_KEY_AI };
    })
);

const checkForTypeahead = () => {
  editor.update(async () => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }
    const node = selection.anchor.getNode();
    const parent = node.getParent();
    if (
      !$isTextNode(node) ||
      !node.isSimpleText() ||
      node.hasFormat("code") ||
      $isTypeaheadNode(node) ||
      $isLinkNode(parent)
    ) {
      return;
    }

    const textContent = node.getTextContent();
    const { offset } = selection.anchor;
    const textBeforeCursor = textContent.substring(0, offset);
    const textAfterCursor = textContent.substring(offset);
    const hasSpaceAfter = textAfterCursor.length === 0 || textAfterCursor.startsWith(" ");

    const escapedTrigger = props.trigger.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const match = textBeforeCursor.match(new RegExp(`(^|[\\s*\\-_~])(${escapedTrigger})([^\\s]*)$`));

    if (!match || !hasSpaceAfter) {
      return;
    }

    const matchStart = match.index ?? 0;
    const splitPoint = matchStart + match[1].length;

    // Don't split if trigger is at beginning of line
    let beforeNode: TextNode | null;
    let afterNode: TextNode | null;
    if (splitPoint === 0) {
      beforeNode = null;
      afterNode = node;
    } else {
      [beforeNode, afterNode] = node.splitText(splitPoint);
    }

    // Extract text between trigger and next space/dot/end
    const textAfterTriggerMatch = textAfterCursor.match(/^[^\s.]*/);
    const textAfterTrigger = textAfterTriggerMatch ? textAfterTriggerMatch[0] : "";

    // Create typeahead node and insert it in the right place
    const typeaheadNode = $createTypeaheadNode(props.trigger, props.trigger + textAfterTrigger);

    const originalStyle = node.getStyle();
    if (originalStyle) {
      typeaheadNode.setStyle(originalStyle);
    }

    if (beforeNode) {
      beforeNode.insertAfter(typeaheadNode);
    } else {
      afterNode.insertBefore(typeaheadNode);
    }
    typeaheadNode.selectEnd();

    // Clean-up trigger character
    const afterText = afterNode?.getTextContent() || "";
    if (afterNode && afterText.startsWith(props.trigger)) {
      afterNode.setTextContent(afterText.substring(props.trigger.length + textAfterTrigger.length));
    }
    const beforeText = beforeNode?.getTextContent() ?? "";
    if (beforeNode && beforeText.startsWith(props.trigger)) {
      beforeNode.setTextContent(beforeText.substring(props.trigger.length));
    }

    typeaheadNodeKey.value = typeaheadNode.getKey();
    // Wait for the DOM to update and then open the dropdown
    await when(() => !!typeaheadNodeKey.value && !!editor.getElementByKey(typeaheadNodeKey.value));
    typeaheadNodeElement.value = editor.getElementByKey(selection.anchor.key);
    query.value = textAfterTrigger;

    if (pageStore.isMobile && typeaheadNodeElement.value) {
      const rect = typeaheadNodeElement.value.getBoundingClientRect();
      maxHeightPixels.value = 192;
      skidding.value = window.innerWidth / 2 - rect.left - rect.width / 2;
    } else {
      maxHeightPixels.value = undefined;
      skidding.value = 0;
    }

    await when(computed(() => dropdownRef.value));

    // If the node is abandoned in the meantime, don't open the dropdown
    if (abandonedNodeKeys.has(typeaheadNodeKey.value)) {
      abandonedNodeKeys.delete(typeaheadNodeKey.value);
      return;
    }
    dropdownRef.value?.open();
  });
};

let ignoreDropdownOpen = false;
const openDropdownIfNeeded = async () => {
  if (ignoreDropdownOpen) {
    ignoreDropdownOpen = false;
    return;
  }
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return;
  }
  const node = selection.anchor.getNode();
  if (node.getKey() === typeaheadNodeKey.value) {
    await when(computed(() => dropdownRef.value));
    dropdownRef.value?.open();
  }
};

const logOpen = () => {
  if (pageStore.isPublic) {
    return;
  }

  backend.event.create(EventKind.USAGE_NLP_TYPEAHEAD_OPEN, { via: props.trigger });
};

const loadMoreEntity = async (key: string) => {
  const item = allOptions.value.find((e) => e.key === key);
  if (!item) {
    return;
  }

  const loadedCount = item.options.length;
  const { total } = item;
  if (!item.loadMore || (total && loadedCount >= total)) {
    return;
  }

  const moreItems = await item.loadMore(loadedCount);
  if (!moreItems) {
    return;
  }

  item.options = [...new Set([...item.options, ...moreItems.options])];
  item.total = moreItems.total;
  allOptionsRaw.value = [...allOptionsRaw.value];
};
const targets = computed(() => Array.from(itemRefMap.values()));
useIntersectionObserver(
  targets,
  (entries) => {
    entries.forEach(async (entry) => {
      const entity = entry?.target?.getAttribute("data-entity");
      const isVisible = entry?.isIntersecting ?? false;
      if (!entity || !isVisible) {
        return;
      }
      loadMoreEntity(entity);
    });
  },
  { root: scrollContainer }
);

const cleanupTypeahead = () => {
  editor.update(() => {
    if (!typeaheadNodeKey.value) {
      return;
    }
    const typeaheadNode = $getNodeByKey(typeaheadNodeKey.value) as TypeaheadNode;
    if (!typeaheadNode) {
      return;
    }

    typeaheadNode.setAbandoned();
    abandonedNodeKeys.add(typeaheadNodeKey.value);
    typeaheadNodeKey.value = null;
    typeaheadNodeElement.value = null;
  });
};

const triggerMatch = computed(() => usePrefixTypeaheadTriggerMatch(props.trigger));

const hasAnyOptions = computed(() => optionsFiltered.value.some((section) => section.options.length > 0));
watch(query, (newQuery, oldQuery) => {
  // Dont leave the typeahead node
  if (newQuery.length === 0 || isLoading.value || hasAnyOptions.value || oldQuery.length >= newQuery.length) {
    return;
  }

  // If there is a match, dont leave the typeahead node
  const match = triggerMatch.value(props.trigger + newQuery);
  if (!match) {
    return;
  }

  cleanupTypeahead();
});

const commandHelper = (event: KeyboardEvent | null): false | TypeaheadNode => {
  const selection = $getSelection();
  if (!event || !$isRangeSelection(selection)) {
    return false;
  }

  const node = selection.anchor.getNode();
  if (node.getKey() !== typeaheadNodeKey.value || !$isTypeaheadNode(node)) {
    return false;
  }

  return node;
};

const handleSpace = (event: KeyboardEvent | null) => {
  const node = commandHelper(event);
  if (!node || !event) {
    return false;
  }

  // If first character is a space, remove typeahead
  if (node.getTextContentSize() === 1 && node.getTextWithoutTrigger() === "") {
    cleanupTypeahead();
    return true;
  }

  const queryNorm = query.value.toLowerCase().trim();
  const filteredOptions = optionsFiltered.value.flatMap(({ options }) => options);

  // If there's exactly one option and it matches the query exactly, select it
  if (
    filteredOptions.length === 1 &&
    filteredOptions[0].label.toLowerCase() === queryNorm &&
    !filteredOptions[0].options &&
    !filteredOptions[0].disabled
  ) {
    event.preventDefault();
    event.stopPropagation();
    onSelectOption(filteredOptions[0]);
    return true;
  }
  return false;
};

const handleArrows = (event: KeyboardEvent | null, command: LexicalCommand<KeyboardEvent>) => {
  const node = commandHelper(event);
  if (!node || !event) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();
  if (!dropdownRef.value?.isOpen) {
    dropdownRef.value?.open();
    return true;
  }
  dropdownRef.value?.moveUpOrDown(command === KEY_ARROW_UP_COMMAND);

  return false;
};

const handleEnter = (event: KeyboardEvent | null) => {
  const node = commandHelper(event);
  if (!node || !event || !dropdownRef.value?.isOpen) {
    return false;
  }

  event.preventDefault();
  event.stopPropagation();
  dropdownRef.value?.clickHighlightedItem();
  return true;
};

const handleClose = (event: KeyboardEvent | null, command: LexicalCommand<KeyboardEvent>) => {
  const node = commandHelper(event);
  if (!node || !event) {
    return false;
  }

  dropdownRef.value?.close();
  ignoreDropdownOpen = true;
  return command === KEY_ESCAPE_COMMAND;
};

let unregisterListeners: () => void;
onMounted(() => {
  if (!editor.hasNodes([TypeaheadNode])) {
    throw new Error("TypeaheadPlugin: TypeaheadNode not registered on editor");
  }

  unregisterListeners = mergeRegister(
    editor.registerUpdateListener(({ editorState, tags }) => {
      if (tags.has("collaboration")) {
        return;
      }
      editorState.read(() => {
        checkForTypeahead();
        openDropdownIfNeeded();
      });
    }),
    editor.registerMutationListener(TypeaheadNode, (mutatedNodes, payload) => {
      if (payload.updateTags.has("collaboration")) {
        return;
      }

      [...mutatedNodes.entries()].forEach(([nodeKey, mutation]) => {
        const resetState = () => {
          typeaheadNodeKey.value = null;
          typeaheadNodeElement.value = null;
          query.value = "";
        };

        editor.read("latest", () => {
          // If the node is destroyed and it is the current typeahead node, reset
          if (mutation === "destroyed" && nodeKey === typeaheadNodeKey.value) {
            resetState();
            return;
          }

          const node = $getNodeByKey(nodeKey);
          if (!(node instanceof TypeaheadNode) || node.isAbandoned()) {
            return;
          }

          // If this node has a different trigger, ignore it (it belongs to another plugin instance)
          if (node.getTrigger() !== props.trigger) {
            return;
          }

          typeaheadNodeKey.value = nodeKey;
          typeaheadNodeElement.value = editor.getElementByKey(nodeKey);
          query.value = node.getTextWithoutTrigger();
          dropdownRef.value?.setIndexOfFocusedItem(0);
          if (mutation === "created" && query.value.length === 0) {
            execute();
          }
        });
      });
    }),
    editor.registerCommand(KEY_ARROW_UP_COMMAND, (e) => handleArrows(e, KEY_ARROW_UP_COMMAND), COMMAND_PRIORITY_LOW),
    editor.registerCommand(
      KEY_ARROW_DOWN_COMMAND,
      (e) => handleArrows(e, KEY_ARROW_DOWN_COMMAND),
      COMMAND_PRIORITY_LOW
    ),
    editor.registerCommand(KEY_ARROW_LEFT_COMMAND, (e) => handleClose(e, KEY_ARROW_LEFT_COMMAND), COMMAND_PRIORITY_LOW),
    editor.registerCommand(
      KEY_ARROW_RIGHT_COMMAND,
      (e) => handleClose(e, KEY_ARROW_RIGHT_COMMAND),
      COMMAND_PRIORITY_LOW
    ),
    editor.registerCommand(KEY_ENTER_COMMAND, (e) => handleEnter(e), COMMAND_PRIORITY_HIGH),
    editor.registerCommand(KEY_SPACE_COMMAND, (e) => handleSpace(e), COMMAND_PRIORITY_LOW),
    editor.registerCommand(KEY_ESCAPE_COMMAND, (e) => handleClose(e, KEY_ESCAPE_COMMAND), COMMAND_PRIORITY_NORMAL),
    editor.registerCommand(
      BLUR_COMMAND,
      () => {
        if (dropdownRef.value?.isOpen) {
          return false;
        }
        ignoreDropdownOpen = true;
        return false;
      },
      COMMAND_PRIORITY_LOW
    )
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});
</script>

<template>
  <DropdownMenu
    v-if="typeaheadNodeElement"
    ref="dropdownRef"
    :reference-node="typeaheadNodeElement ?? undefined"
    :placement="placement"
    :distance="0"
    :skidding="skidding"
    :popper-class="LEXICAL_OUTSIDE_CLASS"
    :loading="isLoading"
    highlight-only
    :sections="sections"
    :width-pixels="wide ? 384 : undefined"
    :max-height-pixels="maxHeightPixels"
    disable-mobile-sheet
    enable-show-more
    @after-open="logOpen" />
</template>
